/**
 * STAGED platform-owner default operation — client view of the server status.
 * Presentation only: the database (`platform_owner_operates`, RLS, triggers)
 * is the sole authority. Anything malformed, for another account or workspace,
 * expired or future-dated is treated as "unknown" and never shows operation.
 */
export interface OperateStatus {
  actor: string;
  instanceId: string;
  enabled: boolean;
  operates: boolean;
  readOnly: boolean;
  visible: boolean;
  session: { id: string; mode: string; viewedUserId: string | null; expiresAt: number } | null;
}

const isStr = (v: unknown): v is string => typeof v === 'string' && v.length > 0;
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';

export function parseOperateStatus(
  raw: unknown,
  expected: { actor: string | null | undefined; instanceId: string | null | undefined },
  now: number,
): OperateStatus | null {
  if (!raw || typeof raw !== 'object' || !expected.actor || !expected.instanceId) return null;
  const r = raw as Record<string, unknown>;
  if (r.actor !== expected.actor || r.instance_id !== expected.instanceId) return null;
  if (r.is_platform_owner !== true) return null;
  if (!isBool(r.enabled) || !isBool(r.operates) || !isBool(r.read_only) || !isBool(r.visible)) return null;
  let session: OperateStatus['session'] = null;
  if (r.session != null) {
    const s = r.session as Record<string, unknown>;
    const exp = isStr(s.expires_at) ? Date.parse(s.expires_at) : NaN;
    if (!isStr(s.id) || !isStr(s.mode) || !Number.isFinite(exp)) return null;
    // Expired: the server would no longer count it; refetch rather than trust.
    if (exp <= now) return null;
    session = { id: s.id, mode: s.mode, viewedUserId: isStr(s.viewed_user_id) ? s.viewed_user_id : null, expiresAt: exp };
  }
  // Internal consistency: operation and a read-only session are exclusive.
  if (r.operates && (r.read_only || !r.enabled)) return null;
  return {
    actor: r.actor as string, instanceId: r.instance_id as string,
    enabled: r.enabled, operates: r.operates, readOnly: r.read_only, visible: r.visible, session,
  };
}

/** Milliseconds until the status must be re-read (session expiry), or null. */
export function operateRefreshDelay(status: OperateStatus | null, now: number): number | null {
  if (!status?.session) return null;
  return Math.max(1000, status.session.expiresAt - now + 500);
}

/**
 * Shared invalidation signal for every place that can change the caller's
 * platform-access state (diagnostic/impersonation begin/end, audited admin
 * wrappers that open/close sessions server-side, participation changes).
 * Subscribers drop their current status immediately and re-read the server.
 * Carries no data and grants nothing.
 */
const platformSessionBus: EventTarget | null = typeof EventTarget !== 'undefined' ? new EventTarget() : null;
const PLATFORM_SESSION_CHANGED = 'platform-session-changed';

export function notifyPlatformSessionChanged(): void {
  platformSessionBus?.dispatchEvent(new Event(PLATFORM_SESSION_CHANGED));
}

export function subscribePlatformSessionChanged(cb: () => void): () => void {
  if (!platformSessionBus) return () => undefined;
  const h = () => cb();
  platformSessionBus.addEventListener(PLATFORM_SESSION_CHANGED, h);
  return () => platformSessionBus.removeEventListener(PLATFORM_SESSION_CHANGED, h);
}
